/**
 * Color manipulation utilities for glassmorphism effects
 * Phase 1: Foundation & Architecture
 */

/**
 * Parse a hex color to RGB components
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  // Remove # if present
  const cleanHex = hex.replace(/^#/, '')
  
  // Handle shorthand (3 chars)
  if (cleanHex.length === 3) {
    const r = parseInt(cleanHex.charAt(0) + cleanHex.charAt(0), 16)
    const g = parseInt(cleanHex.charAt(1) + cleanHex.charAt(1), 16)
    const b = parseInt(cleanHex.charAt(2) + cleanHex.charAt(2), 16)
    return { r, g, b }
  }
  
  // Handle full hex (6 chars)
  if (cleanHex.length === 6) {
    const r = parseInt(cleanHex.substring(0, 2), 16)
    const g = parseInt(cleanHex.substring(2, 4), 16)
    const b = parseInt(cleanHex.substring(4, 6), 16)
    return { r, g, b }
  }
  
  return null
}

/**
 * Convert RGB to hex
 */
function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) => n.toString(16).padStart(2, '0')
  return `#${toHex(Math.round(r))}${toHex(Math.round(g))}${toHex(Math.round(b))}`
}

/**
 * Lighten a color by a percentage
 * @param color - Hex color (#rgb or #rrggbb)
 * @param percent - Percentage to lighten (0-100)
 */
export function lighten(color: string, percent: number): string {
  const rgb = hexToRgb(color)
  if (!rgb) return color
  
  const factor = 1 + (percent / 100)
  const r = Math.min(255, rgb.r * factor)
  const g = Math.min(255, rgb.g * factor)
  const b = Math.min(255, rgb.b * factor)
  
  return rgbToHex(r, g, b)
}

/**
 * Darken a color by a percentage
 * @param color - Hex color (#rgb or #rrggbb)
 * @param percent - Percentage to darken (0-100)
 */
export function darken(color: string, percent: number): string {
  const rgb = hexToRgb(color)
  if (!rgb) return color
  
  const factor = 1 - (percent / 100)
  const r = Math.max(0, rgb.r * factor)
  const g = Math.max(0, rgb.g * factor)
  const b = Math.max(0, rgb.b * factor)
  
  return rgbToHex(r, g, b)
}

/**
 * Adjust opacity of a color (for rgba)
 * @param color - Hex color
 * @param opacity - Opacity value (0-1)
 */
export function withOpacity(color: string, opacity: number): string {
  const rgb = hexToRgb(color)
  if (!rgb) return color
  
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`
}

/**
 * Get the glow color for a bubble based on its urgency zone
 */
export function getBubbleGlowColor(
  isInPanicZone: boolean,
  isInMiddleZone: boolean,
  bubbleColor?: string
): string {
  if (isInPanicZone) {
    return 'rgba(239, 68, 68, 0.6)'
  }
  
  if (isInMiddleZone) {
    return 'rgba(249, 115, 22, 0.4)'
  }
  
  // Default outer zone
  return bubbleColor ? withOpacity(bubbleColor, 0.3) : 'rgba(59, 130, 246, 0.3)'
}

/**
 * Create a radial gradient string for canvas
 */
export function createRadialGradient(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  colorStart: string,
  colorEnd: string
): CanvasGradient {
  const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius)
  gradient.addColorStop(0, colorStart)
  gradient.addColorStop(1, colorEnd)
  return gradient
}

/**
 * Parse CSS custom property value
 */
export function getCssVariable(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
}

/**
 * Convert HSL to RGB
 * Useful for creating color variations
 */
export function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  s /= 100
  l /= 100
  
  const k = (n: number) => (n + h / 30) % 12
  const a = s * Math.min(l, 1 - l)
  const f = (n: number) => 
    l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))
  
  return {
    r: Math.round(f(0) * 255),
    g: Math.round(f(8) * 255),
    b: Math.round(f(4) * 255),
  }
}

/**
 * Get contrast color (black or white) for text on a background
 */
export function getContrastColor(backgroundColor: string): '#000000' | '#ffffff' {
  const rgb = hexToRgb(backgroundColor)
  if (!rgb) return '#000000'
  
  // Calculate luminance
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255
  
  return luminance > 0.5 ? '#000000' : '#ffffff'
}
